import { useState, useEffect, useTransition, useCallback, useRef } from "react"
import { faTimes } from "@fortawesome/free-solid-svg-icons"
import { usePaginationFragment } from "react-relay"
import graphql from "babel-plugin-relay/macro"
import { useTranslation } from "react-i18next"
import NewBoxButton from "../Radiate/NewBoxButton/NewBoxButton"
import * as Styles from "./CallHistoryListStyle.js"
import { LoadingMask } from "../../CommonStyles"
import { BetaBadge } from "../VoiceCalling/CallingThreadStyle.js"

function formatTimestamp(createdAt) {
  const date = new Date(Number(createdAt));
  const now = new Date();

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);

  const diffInMs = startOfToday - date;
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  // if today
  if (date >= startOfToday) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  // if yesterday
  if (date >= startOfYesterday && date < startOfToday) {
    return "Yesterday";
  }

  // if within a week
  if (diffInDays < 7) {
    return date.toLocaleDateString([], { weekday: 'long' });
  }

  // if older than a week
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

const CallHistoryList = ({
  open = false,
  onClose = () => { },
  onChangeThreadId,
  ...props
}) => {
  const [statusFilter, setStatusFilter] = useState(null)
  const [loading, startTransition] = useTransition()

  const {
    data,
    loadNext,
    hasNext,
    isLoadingNext,
    refetch
  } = usePaginationFragment(
    graphql`
      fragment CallHistoryList_userViewer on UserScope
      @argumentDefinitions(
        callType: { type: "CallType" }
        first: { type: "Int", defaultValue: 10 }
        after: { type: "String" }
        open: { type: "Boolean", defaultValue: false }
      )
      @refetchable(queryName: "CallHistoryListPaginateQuery") {
        calls(
          callType: $callType
          first: $first
          after: $after
        ) @include(if: $open) @connection(key: "CallHistoryList_calls") {
          edges {
            node {
              id
              callId
              status
              wAppId
              threadId
              missed
              direction
              from
              to
              duration
              callStartAt
              callEndAt
              wMemberId
              wChannel {
                name
              }
              member {
                externalId
                profile {
                  name
                  profilePicUrl
                }
                inboxProfile {
                  name
                  profilePicUrl
                }
                externalProfile 
              }
              wChannelId
              timestamp
              createdAt
              updatedAt
            }
          }
        }
      }
    `,
    props.data.userViewer
  )

  useEffect(() => {
    if (!open) {
      setStatusFilter(null)
    }
    startTransition(() => {
      refetch({
        open,
      }, {
        fetchPolicy: "network-only",
      })
    })
  }, [open, refetch])

  useEffect(() => {
    startTransition(() => {
      refetch({
        callType: statusFilter,
        open: true,
      }, {
        fetchPolicy: "network-only",
      })
    })
  }, [statusFilter, refetch])

  const showLoading = !data?.calls?.edges || loading
  const callList = data?.calls?.edges?.map((edge) => edge.node) || []

  const scrollContainerRef = useRef(null)

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return
    const { scrollTop, scrollHeight, clientHeight } = container
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 150
    if (isNearBottom && hasNext && !isLoadingNext) {
      loadNext(10)
    }
  }, [hasNext, isLoadingNext, loadNext])
  
  return (
    <>
      <Styles.CallHistoryListContainer show={open}>
        <Styles.CallHistoryListTopContainer>
          <NewBoxButton
            className="close-button"
            borderless
            icon={faTimes}
            onClick={() => {
              onClose();
            }}
          />
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontWeight: 600,
                fontSize: "18px",
              }}
            >
              WhatsApp Calls <BetaBadge>BETA</BetaBadge>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Styles.FilterButton
                type="button"
                active={statusFilter === "INCOMING"}
                title="Incomming Calls"
                onClick={() => setStatusFilter(statusFilter === "INCOMING" ? null : "INCOMING")}
              >
                <IncomingCallIcon />
              </Styles.FilterButton>

              <Styles.FilterButton
                type="button"
                active={statusFilter === "OUTGOING"}
                title="Outgoing Calls"
                onClick={() => setStatusFilter(statusFilter === "OUTGOING" ? null : "OUTGOING")}
              >
                <OutgoingCallIcon />
              </Styles.FilterButton>

              <Styles.FilterButton
                type="button"
                active={statusFilter === "MISSED"}
                title="Missed Calls"
                onClick={() => setStatusFilter(statusFilter === "MISSED" ? null : "MISSED")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 640 640"
                >
                  <path d="M184.2 64C170.9 64 160.2 74.7 160.2 88L160.2 189.8C160.2 203.1 170.9 213.8 184.2 213.8C197.5 213.8 208.2 203.1 208.2 189.8L208.2 145.9L303.2 240.9C312.6 250.3 327.8 250.3 337.1 240.9L433.1 144.9C442.5 135.5 442.5 120.3 433.1 111C423.7 101.7 408.5 101.6 399.2 111L320.2 190L242.1 111.9L286 111.9C299.3 111.9 310 101.2 310 87.9C310 74.6 299.3 64 286 64L184.2 64zM551.3 575.6C570.1 583.4 591.8 575.9 601.9 558.2L604.8 553.2C638 495 632.8 411.8 567.5 369.3C417.6 271.8 222.8 271.8 72.9 369.3C7.5 411.8 2.4 495.1 35.6 553.2L38.5 558.2C48.6 575.9 70.3 583.3 89.1 575.6L186.6 535.5C203.2 528.7 213.3 511.8 211.5 494L205.4 433.3C279.7 408.2 361 409.6 434.6 437.2L429 493.9C427.2 511.7 437.3 528.6 453.9 535.4L551.4 575.5z" />
                </svg>
              </Styles.FilterButton>
            </div>
          </div>
        </Styles.CallHistoryListTopContainer>
        <Styles.ContentContainer ref={scrollContainerRef} onScroll={handleScroll}>
          {showLoading && (
            <Styles.LoadingContainer>
              <LoadingMask style={{ height: "100%", width: "100%" }}>
                <div className="line"></div>
                <div className="line"></div>
                <div className="line"></div>
              </LoadingMask>
            </Styles.LoadingContainer>
          )}
          
          {!showLoading && callList.length === 0 && <CallListEmptyState />}
          {!showLoading && callList.length > 0 && (
            <Styles.RowContainer>
              {callList.length > 0 &&
                callList.map((call) => (
                  <CallHistoryRow key={call.callId} call={call} onChangeThreadId={onChangeThreadId} />
                ))}
            </Styles.RowContainer>
          )}

        </Styles.ContentContainer>
      </Styles.CallHistoryListContainer>
    </>
  )
}

const CallListEmptyState = () => {
  const { t } = useTranslation("common")
  return (
    <Styles.EmptyState>
      {t?.("calling.no_calls_now_part_1")}<br /><br />{t?.("calling.no_calls_now_part_2")}
    </Styles.EmptyState>
  )
}

const CallHistoryRow = ({ call, onChangeThreadId }) => {
  const getCallDetails = (call) => {
    const isMissedCall = call.missed
    const isOutgoingCall = call.direction === "BUSINESS_INITIATED"
    const isIncomingCall = call.direction === "USER_INITIATED"
    
    const externalProfile = call?.member?.externalProfile
    const profile = call?.member?.profile
    const inboxProfile = call?.member?.inboxProfile

    const contactNumber = call?.member?.externalId
    const contactName = externalProfile?.name || externalProfile?.firstName || profile?.name || inboxProfile?.name
    const contactImageUrl = profile?.profilePicUrl || inboxProfile?.profilePicUrl

    const threadId = call?.threadId

    const durationInSeconds = Number(call?.duration);
    const minutes = Math.floor(durationInSeconds / 60);
    const seconds = durationInSeconds % 60;
    const formattedDuration = `${minutes}:${seconds.toString().padStart(2, '0')}`;


    // call status message
    let statusMessage = ""
    if (isMissedCall) {
      statusMessage = `You missed a WhatsApp call from ${contactNumber}`
    } else if (isIncomingCall) {
      statusMessage = "Incoming call"
    } else if (isOutgoingCall) {
      statusMessage = "Outgoing call"
    }
    
    return {
      statusMessage,
      isIncomingCall,
      isOutgoingCall,
      isMissedCall,
      contactNumber,
      contactName: contactName || contactNumber,
      contactImageUrl: contactImageUrl,
      channelId: call.wChannelId,
      channelName: call?.wChannel?.name || call?.wChannelId,
      iconColor: "#B9B9B9",
      threadId,
      duration: call?.duration ? `- Duration: ${formattedDuration} ${durationInSeconds < 60 ? "sec": "min"}.`: null
    }
  }

  const callDetails = getCallDetails(call)
  const formattedDate = formatTimestamp(call?.timestamp || call?.createdAt)

  return (
    <Styles.CallHistoryCard onClick={() => onChangeThreadId(callDetails.threadId)}>
      {callDetails?.contactImageUrl ? 
        <Styles.UserImage src={callDetails.contactImageUrl} /> : 
        (<Styles.UserImageFallback>
        <UserIcon />
      </Styles.UserImageFallback>)}
      
      <Styles.CallHistoryRow>
        <Styles.ContactName>{callDetails.contactName || callDetails.contactNumber}</Styles.ContactName>

        <Styles.IntegrationBadge>
            <WhatsAppIcon size={14} />
            <span>{callDetails.channelName || callDetails.channelId}</span>
          </Styles.IntegrationBadge>

        <Styles.CallStatusMessage>
          <Styles.CallStatusIcon iconColor={callDetails.iconColor}>
            {!callDetails.isMissedCall && callDetails.isIncomingCall && <IncomingCallIcon />}
            {!callDetails.isMissedCall && callDetails.isOutgoingCall && <OutgoingCallIcon />}
            {callDetails.isMissedCall && <MissedCallIcon />}
          </Styles.CallStatusIcon>
          <span title={callDetails.statusMessage}>
            {callDetails.statusMessage.length > 40
              ? callDetails.statusMessage.substring(0, 30) + "..."
              : callDetails.statusMessage}
              <span style={{fontStyle: "italic"}}>{callDetails.duration}</span>
          </span>
        </Styles.CallStatusMessage>
      </Styles.CallHistoryRow>

      <Styles.RightSection>
        <Styles.DateText>{formattedDate}</Styles.DateText>
        {callDetails.isMissedCall && <Styles.MissedCallIndicator />}
      </Styles.RightSection>
    </Styles.CallHistoryCard>
  )
}

const WhatsAppIcon = ({size = 20}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    viewBox="0 0 584 584"
    height={size}
    width={size}
  >
    <defs>
      <linearGradient
        id="linear-gradient"
        x1="292"
        y1="584"
        x2="292"
        y2="0"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#25cf43" />
        <stop offset="1" stopColor="#61fd7d" />
      </linearGradient>
      <style>{`
        .cls-1 { fill: #fff; }
        .cls-1, .cls-2 { fill-rule: evenodd; }
        .cls-2 { fill: url(#linear-gradient); }
      `}</style>
    </defs>
    <g id="WhatsApp_Logo_Icon">
      <g id="Icon_Shape">
        <path
          className="cls-2"
          d="M584,435.8c0,3.2,0,10.1-.3,15.5-.5,13.1-1.5,30-3.1,37.7-2.4,11.6-5.9,22.5-10.6,31.6-5.5,10.8-12.5,20.4-20.9,28.7-8.3,8.3-17.9,15.3-28.7,20.8-9.1,4.7-20.1,8.2-31.8,10.6-7.6,1.5-24.4,2.6-37.4,3-5.4.2-12.3.3-15.5.3H148.2c-3.2,0-10.1-.1-15.5-.3-13.1-.5-30-1.5-37.7-3.1-11.6-2.4-22.5-5.9-31.6-10.6-10.8-5.5-20.4-12.5-28.7-20.9-8.3-8.3-15.3-17.9-20.8-28.7-4.7-9.1-8.2-20.1-10.6-31.8-1.5-7.6-2.6-24.4-3-37.4-.2-5.4-.3-12.3-.3-15.5V148.2c0-3.2.1-10.1.3-15.5.5-13.1,1.5-30,3.1-37.7,2.4-11.6,5.9-22.5,10.6-31.6,5.5-10.8,12.5-20.4,20.9-28.7,8.3-8.3,17.9-15.3,28.7-20.8,9.1-4.7,20.1-8.2,31.8-10.6,7.6-1.5,24.4-2.6,37.4-3,5.4-.2,12.3-.3,15.5-.3h287.6c3.2,0,10.1.1,15.5.3,13.1.5,30,1.5,37.7,3.1,11.6,2.4,22.5,5.9,31.6,10.6,10.8,5.5,20.4,12.5,28.7,20.9,8.3,8.3,15.3,17.9,20.8,28.7,4.7,9.1,8.2,20.1,10.6,31.8,1.5,7.6,2.6,24.4,3,37.4.2,5.4.3,12.3.3,15.5v287.6Z"
        />
      </g>
      <g id="WA_Logo">
        <path
          className="cls-1"
          d="M445.6,139.8c-39.3-39.3-91.6-61-147.3-61-114.8,0-208.1,93.4-208.2,208.1,0,36.7,9.6,72.5,27.8,104l-29.5,107.8,110.4-28.9c30.4,16.6,64.6,25.3,99.5,25.3h0s0,0,0,0c114.7,0,208.1-93.4,208.2-208.1,0-55.6-21.6-107.9-60.9-147.2ZM298.4,460h0c-31,0-61.5-8.4-88.1-24.1l-6.3-3.7-65.5,17.2,17.5-63.8-4.1-6.5c-17.3-27.5-26.5-59.4-26.5-92.1,0-95.4,77.7-173,173.1-173,46.2,0,89.7,18,122.3,50.7,32.7,32.7,50.7,76.2,50.6,122.4,0,95.4-77.7,173-173,173ZM393.3,330.5c-5.2-2.6-30.8-15.2-35.5-16.9-4.8-1.7-8.2-2.6-11.7,2.6-3.5,5.2-13.4,16.9-16.5,20.4-3,3.5-6.1,3.9-11.3,1.3-5.2-2.6-22-8.1-41.8-25.8-15.5-13.8-25.9-30.8-28.9-36s-.3-8,2.3-10.6c2.3-2.3,5.2-6.1,7.8-9.1,2.6-3,3.5-5.2,5.2-8.7,1.7-3.5.9-6.5-.4-9.1-1.3-2.6-11.7-28.2-16-38.6-4.2-10.1-8.5-8.8-11.7-8.9-3-.2-6.5-.2-10-.2s-9.1,1.3-13.9,6.5c-4.8,5.2-18.2,17.8-18.2,43.4s18.6,50.3,21.2,53.8c2.6,3.5,36.7,56,88.9,78.5,12.4,5.4,22.1,8.6,29.7,11,12.5,4,23.8,3.4,32.8,2.1,10-1.5,30.8-12.6,35.1-24.7,4.3-12.1,4.3-22.6,3-24.7-1.3-2.2-4.8-3.5-10-6.1Z"
        />
      </g>
    </g>
  </svg>
)

const UserIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
    viewBox="0 0 496 512"
  >
    <path d="M248 8C111 8 0 119 0 256s111 248 248 248 248-111 248-248S385 8 248 8zm0 96c48.6 0 88 39.4 88 88s-39.4 88-88 88-88-39.4-88-88 39.4-88 88-88zm0 344c-58.7 0-111.3-26.6-146.5-68.2 18.8-35.4 55.6-59.8 98.5-59.8 2.4 0 4.8 .4 7.1 1.1 13 4.2 26.6 6.9 40.9 6.9 14.3 0 28-2.7 40.9-6.9 2.3-.7 4.7-1.1 7.1-1.1 42.9 0 79.7 24.4 98.5 59.8C359.3 421.4 306.7 448 248 448z" />
  </svg>
)

const IncomingCallIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
    viewBox="0 0 640 640"
  >
    <path d="M176.1 65.4C195.8 60 216.4 70.1 224.2 88.9L264.7 186.2C271.6 202.7 266.8 221.8 252.9 233.2L208.8 269.3C241.3 340.9 297.8 399.3 368.1 434.2L406.8 387C418.1 373.1 437.2 368.4 453.8 375.2L551 415.8C569.8 423.6 579.9 444.2 574.5 463.9L573 469.4C555.4 534.1 492.9 589.3 416.6 573.2C241.6 536.1 103.9 398.4 66.8 223.4C50.7 147.1 105.9 84.6 170.5 66.9L176 65.4zM569 105L466 208L520.1 208C533.4 208 544.1 218.7 544.1 232C544.1 245.3 533.4 256 520.1 256L408.1 256C394.8 256 384.1 245.3 384.1 232L384.1 120C384.1 106.7 394.8 96 408.1 96C421.4 96 432.1 106.7 432.1 120L432.1 174.1L535.1 71.1C544.5 61.7 559.7 61.7 569 71.1C578.3 80.5 578.4 95.7 569 105z" />
  </svg>
)

const OutgoingCallIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
    viewBox="0 0 640 640"
  >
    <path d="M176.1 65.4C195.8 60 216.4 70.1 224.2 88.9L264.7 186.2C271.6 202.7 266.8 221.8 252.9 233.2L208.8 269.3C241.3 340.9 297.8 399.3 368.1 434.2L406.8 387C418.1 373.1 437.2 368.4 453.8 375.2L551 415.8C569.8 423.6 579.9 444.2 574.5 463.9L573 469.4C555.4 534.1 492.9 589.3 416.6 573.2C241.6 536.1 103.9 398.4 66.8 223.4C50.7 147.1 105.9 84.6 170.5 66.9L176 65.4zM391 215L494 112L439.9 112C426.6 112 415.9 101.3 415.9 88C415.9 74.7 426.7 64 440 64L552 64C565.3 64 576 74.7 576 88L576 200C576 213.3 565.3 224 552 224C538.7 224 528 213.3 528 200L528 145.9L425 248.9C415.6 258.3 400.4 258.3 391.1 248.9C381.8 239.5 381.7 224.3 391.1 215z" />
  </svg>
)

const MissedCallIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
    viewBox="0 0 640 640"
  >
    <path d="M184.2 64C170.9 64 160.2 74.7 160.2 88L160.2 189.8C160.2 203.1 170.9 213.8 184.2 213.8C197.5 213.8 208.2 203.1 208.2 189.8L208.2 145.9L303.2 240.9C312.6 250.3 327.8 250.3 337.1 240.9L433.1 144.9C442.5 135.5 442.5 120.3 433.1 111C423.7 101.7 408.5 101.6 399.2 111L320.2 190L242.1 111.9L286 111.9C299.3 111.9 310 101.2 310 87.9C310 74.6 299.3 64 286 64L184.2 64zM551.3 575.6C570.1 583.4 591.8 575.9 601.9 558.2L604.8 553.2C638 495 632.8 411.8 567.5 369.3C417.6 271.8 222.8 271.8 72.9 369.3C7.5 411.8 2.4 495.1 35.6 553.2L38.5 558.2C48.6 575.9 70.3 583.3 89.1 575.6L186.6 535.5C203.2 528.7 213.3 511.8 211.5 494L205.4 433.3C279.7 408.2 361 409.6 434.6 437.2L429 493.9C427.2 511.7 437.3 528.6 453.9 535.4L551.4 575.5z" />
  </svg>
)

export default CallHistoryList
